import { ApolloClient, gql, type NormalizedCacheObject } from '@apollo/client/core';
import type {
  DeletePresaleMutationInput,
  Mutation,
  Query,
  SavePresaleMutationInput,
} from '../../../generated/api.graphql';
import * as VPErrors from '../../../VPError';
import type { VPResult } from '../../VPResult';
import { VP_ERROR_FIELDS } from '../../VPError/fragments';
import * as Presale from './Presale';
import * as PresaleInput from './PresaleInput';
import { PRESALE_FIELDS } from './fragments';

/**
 * Presales on the tenant (AppSchema) connection: the ticketing modal's Presales tab reads them
 * off the event and writes them through the two root mutations.
 *
 * **Nothing here catches.** A transport failure throws and propagates; `VPResult`'s
 * `success: false` covers only the GraphQL-level outcomes — an absent payload, a populated
 * `errors` list. That matches `App/Venue` and `App/WidgetConfig`.
 *
 * **No cache invalidation, deliberately.** `savePresale`'s payload writes the normalized `Presale`
 * entity, so an *update* is reflected wherever it is already rendered; a *create* and a *delete*
 * change the event's `presales` list, which no payload carries. `deletePresale` receives only an
 * id and so cannot identify the parent `Event` to evict, and a half-applied eviction is worse than
 * none — so the `network-only` read below is the refresh path, as it is for `App/Venue` and
 * `App/WidgetConfig`.
 */

const failure = (message: string): VPResult<never, VPErrors.VPError[]> => ({
  success: false,
  errors: [{ path: '', message }],
});

/**
 * There is no top-level `presales` field, so this is a nested read off `event(id:)`.
 *
 * The event's own `id` is selected beside the list because this and `App/Event`'s
 * `EVENT_DETAIL_QUERY` resolve the same `event(id:)` field on the same `gqlApiClient` cache:
 * without it the result cannot normalize onto the existing `Event` entity, so Apollo writes a
 * second unidentified copy and warns about replacing the field. That is the rule
 * `doc/claude/api-layer.md` records for the two `eventArtists` selections. The rows carry `id`
 * too, so they normalize as `Presale` entities and the save payload's write lands on the same
 * ones.
 *
 * `$eventId` is declared `Int!` even though the root argument is a nullable `Int` — the same
 * declaration `EVENT_DETAIL_QUERY` uses; a non-null variable satisfies a nullable argument, and
 * this read has no slug form.
 */
export const EVENT_PRESALES_QUERY = gql`
  query EventPresales($eventId: Int!) {
    event(id: $eventId) {
      id
      presales {
        ...PresaleFields
      }
    }
  }
  ${PRESALE_FIELDS}
`;

export const SAVE_PRESALE_MUTATION = gql`
  mutation savePresale($input: SavePresaleMutationInput!) {
    savePresale(input: $input) {
      presale {
        ...PresaleFields
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${PRESALE_FIELDS}
  ${VP_ERROR_FIELDS}
`;

export const DELETE_PRESALE_MUTATION = gql`
  mutation deletePresale($input: DeletePresaleMutationInput!) {
    deletePresale(input: $input) {
      success
      errors {
        ...VPErrorFields
      }
    }
  }
  ${VP_ERROR_FIELDS}
`;

/**
 * `network-only`, as `App/Event`'s `fetchEvent` is: the presales tab refetches after a save or a
 * delete, and a cached list would answer it with the state it just changed.
 */
export const fetchPresales = async (
  client: ApolloClient<NormalizedCacheObject>,
  eventId: number,
): Promise<Presale.Presale[]> => {
  const { data } = await client.query<Query>({
    query: EVENT_PRESALES_QUERY,
    variables: { eventId },
    fetchPolicy: 'network-only',
  });

  // `event` is nullable — a missing event is a failed read, not an event with no presales, and
  // resolving `[]` would render "no presales" over it. `fetchEvent` throws for the same reason.
  if (!data.event) {
    throw new Error('Failed to fetch presales.');
  }

  // `presales` is `[Presale!]!`, so there is nothing to default here.
  return data.event.presales.map(Presale.fromGql);
};

/** Creates when the input carries no `id`, updates when it does. */
export const savePresale = async (
  client: ApolloClient<NormalizedCacheObject>,
  presale: PresaleInput.PresaleInput,
): Promise<VPResult<Presale.Presale, VPErrors.VPError[]>> => {
  // Typed rather than inlined: the `mutate<Mutation>` generic does not check `variables`.
  const input: SavePresaleMutationInput = { presale: PresaleInput.toGqlInput(presale) };

  const { data } = await client.mutate<Mutation>({
    mutation: SAVE_PRESALE_MUTATION,
    variables: { input },
  });

  const payload = data?.savePresale;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  // This payload carries no `success`, and its `errors` is nullable — hence the `?? []` above,
  // which covers both an empty array and an absent key. So an empty error list with no entity is
  // the one shape that would otherwise read as a silent success.
  if (!payload?.presale) {
    return failure('Failed to save the presale.');
  }

  return { success: true, data: Presale.fromGql(payload.presale) };
};

export const deletePresale = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: number,
): Promise<VPResult<null, VPErrors.VPError[]>> => {
  // The argument is named `id` — its `deleteDiscount` sibling on this schema takes `discountId`,
  // so don't copy that name here — and it is an `Int!`, not the `ID!` `deleteVenue` takes, so the
  // caller's number goes over untouched.
  const input: DeletePresaleMutationInput = { id };

  const { data } = await client.mutate<Mutation>({
    mutation: DELETE_PRESALE_MUTATION,
    variables: { input },
  });

  const payload = data?.deletePresale;

  // Unlike `savePresale`'s, this payload does carry `success`, so callers branch on it.
  if (payload?.success) {
    return { success: true, data: null };
  }

  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  // A failure the caller cannot render is worse than a generic message.
  return errors.length ? { success: false, errors } : failure('Failed to delete the presale.');
};
